import axios from './axios-retried';
import { logoutUser } from './screens/auth/actions';
import { Platform } from 'react-native';
import AuthService from './services/auth_service.js';
import { REFRESH_USER_TOKEN } from './actions/actionTypes';
import Utils from './utils';
const KeychainError = require('./errors/KeychainError');
import { NEEDS_MFA_SETUP, NEEDS_MFA_CODE } from './constants/auth_codes';

const AWS_REGEX = /https:\/\/.*\.amazonaws\.com/;
const AZURE_REGEX = /https:\/\/.*\.blob\.core\.windows\.net/;
const AZURE_GOV_REGEX = /https:\/\/.*\.blob\.core\.usgovcloudapi\.net/;

let lastRefreshDate = Date.now();
const deltaMillis = 10 * 60 * 1000;

let isRefreshing = false;
let refreshSubscribers = [];
const MFA_ERROR_MESSAGE = 'cant login with stored creds - mfa required';
function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}
function onTokenRefreshed() {
  refreshSubscribers.map((cb) => cb());
}

export default {
  setupInterceptors: (store) => {
    // Add request interceptor to handle auth and error logging
    axios.interceptors.request.use(
      (conf) => {
        let token = store.getState().user.token || store.getState().user.oktaAuthToken;
        conf.headers['op-platform'] = Platform.OS === 'web' ? 'web' : 'native';

        // Check if the user has a token to sign the request with
        if (token) {
          if (conf.method !== 'OPTIONS') {
            // S3 does not need the user token
            if (
              conf.url.match(AWS_REGEX) ||
              conf.url.match(AZURE_REGEX) ||
              conf.url.match(AZURE_GOV_REGEX)
            ) {
              // do nothing
            } else {
              // everything else does, so set the user bearer token
              conf.headers.authorization = 'Bearer ' + token;
              const nonce = store.getState().user.oktaNonce;
              if (nonce) {
                conf.headers['nonce'] = nonce;
                if (store.getState().user.userId) {
                  conf.headers['op-user-id'] = store.getState().user.userId;
                }
              }
            }
          }
        }
        return conf;
      },
      (error) => Promise.reject(error)
    );

    // Add a response interceptor
    axios.interceptors.response.use(
      (response) => {
        // const detailedErrorInfo = {
        //   userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        //   platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
        //   screenSize:
        //     typeof window !== 'undefined'
        //       ? `${window.screen.width}x${window.screen.height}`
        //       : 'unknown',
        //   timestamp: new Date().toISOString(),
        // };
        // console.error('Testing network error logging', {
        //   message: 'Just testing: No response text available',
        //   detailedErrorInfo,
        //   originalError: new Error('Just testing: No response text available'),
        // });

        // only if its not okta auth
        // skip if this is a login or register call, or if were not logged in
        let token = store.getState().user.token;
        if (token && !response.data?.token) {
          const nowDate = Date.now();
          if (nowDate - lastRefreshDate >= deltaMillis) {
            AuthService.refreshAuthToken()
              .then((result) => {
                return store.dispatch({
                  type: REFRESH_USER_TOKEN,
                  payload: result,
                });
              })
              .catch((e) => {
                store.dispatch(logoutUser(false));
              });
            lastRefreshDate = Date.now();
          }
        }
        return response;
      },

      (error) => {
        const originalRequest = error.config;

        // include config in the response for use by other interceptors
        // (axios-retry uses a response interceptor)
        let formattedOriginalRequest = null;
        const errorResponseObj = {
          response: {},
          config: originalRequest,
        };

        if (originalRequest) {
          let formData = originalRequest.data;
          // put try catch in case originalRequest.data is not a valid json
          try {
            formData = Utils.sanitizeObject(JSON.parse(originalRequest.data), ['password']);
          } catch (e) {
            // do nothing
          }
          formattedOriginalRequest = {
            url: originalRequest.url,
            retryCount: (originalRequest['axios-retry'] || {}).retryCount,
            // headers: originalRequest.headers,
            params: originalRequest.params,
            data: formData,
          };
          errorResponseObj.originalRequest = formattedOriginalRequest;
        }

        // https://github.com/axios/axios#handling-errors
        if (error.response) {
          if (error.response?.status === 401) {
            if (Platform.OS === 'web') {
              store.dispatch(logoutUser());
              // native apps, non okta flow only attempt to refresh the login from keychain creds
            } else if (store.getState().user.token) {
              // GO THROUGH REFRESH FLOW
              if (!isRefreshing) {
                isRefreshing = true;
                AuthService.loginWithStoredCreds()
                  .then((result) => {
                    if (result.message === NEEDS_MFA_SETUP || result.message === NEEDS_MFA_CODE) {
                      throw new Error(MFA_ERROR_MESSAGE);
                    }
                    return store.dispatch({
                      type: REFRESH_USER_TOKEN,
                      payload: result,
                    });
                  })
                  .then(() => {
                    isRefreshing = false;
                    onTokenRefreshed();
                    refreshSubscribers = [];
                  })
                  .catch((error) => {
                    isRefreshing = false;
                    refreshSubscribers = [];
                    if (error.message !== MFA_ERROR_MESSAGE) {
                      console.error('Network refresh token error', error);
                    }
                    let disableToken = true;
                    if (error instanceof KeychainError || error.message === MFA_ERROR_MESSAGE) {
                      disableToken = false;
                    }
                    return store.dispatch(logoutUser(disableToken));
                  });
              }
              return new Promise((resolve, reject) => {
                subscribeTokenRefresh(() => {
                  // retry failed request
                  resolve(axios(originalRequest));
                });
              });
            }
          }
          if (error.response && error.response.data) {
            errorResponseObj.response.data = error.response.data;
            errorResponseObj.response.httpStatusCode =
              error.response.httpStatusCode || error.response.status;
            if (error.response.data.message) {
              errorResponseObj.message = error.response.data.message;
            }
            if (error.response.data.status) {
              errorResponseObj.dataStatus = error.response.data.status;
            }
          } else {
            errorResponseObj.response = error.response;
            if (error.response.message) {
              errorResponseObj.message = error.response.message;
            }
          }
          return Promise.reject(errorResponseObj);
        } else if (error.request) {
          // The request was made but no response was received
          // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          const detailedErrorInfo = {
            status: error.request?.status,
            readyState: error.request?.readyState,
            responseText: error.request?.responseText,
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
            platform: typeof navigator !== 'undefined' ? navigator.platform : 'unknown',
            screenSize:
              typeof window !== 'undefined'
                ? `${window.screen?.width}x${window.screen?.height}`
                : 'unknown',
            timestamp: new Date().toISOString(),
          };

          // Log the error with context to Datadog via console.error
          console.error('Network error with no response from server', {
            message:
              error.request?.responseText ||
              (typeof error.request === 'string'
                ? error.request
                : error.request?.toString?.() || 'No response text available'),
            detailedErrorInfo,
            originalError: error,
          });

          errorResponseObj.message =
            detailedErrorInfo.responseText ||
            'Network error: No response received and no responseText available.';

          return Promise.reject(errorResponseObj);
        }
        // Something happened in setting up the request that triggered an Error
        return Promise.reject({ message: error.message });
      }
    );
  },
};
